import type { FormEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { AxiosError } from "axios";
import { useAdminResetOwnerPassword, useSearchOwnerAccounts } from "../hooks";
import type { OwnerSearchItem } from "../api";

type ErrorPayload = {
  detail?: string;
  message?: string;
};

type AdminResetOwnerPasswordFormProps = {
  embedded?: boolean;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError<ErrorPayload>;
    return (
      axiosError.response?.data?.detail ??
      axiosError.response?.data?.message ??
      axiosError.message ??
      "Unable to reset owner password right now."
    );
  }
  if (error instanceof Error) return error.message;
  return "Unable to reset owner password right now.";
};

const DEBOUNCE_MS = 300;

export const AdminResetOwnerPasswordForm = ({ embedded }: AdminResetOwnerPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ email: string; temp_password: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useAdminResetOwnerPassword();
  const { data: suggestions = [], isFetching, isError } = useSearchOwnerAccounts(debouncedQuery);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(email.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [email]);

  const canSubmit = useMemo(() => {
    return email.trim().length > 0 && !mutation.isPending;
  }, [email, mutation.isPending]);

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box" as const,
    padding: "0.6rem 2.25rem 0.6rem 0.75rem",
    borderRadius: "0.55rem",
    border: "1px solid #d1d9e6",
    fontSize: "0.92rem"
  };

  const closeSuggestions = useCallback(() => {
    setSuggestionsOpen(false);
    setActiveIndex(-1);
  }, []);

  const selectOwner = useCallback(
    (item: OwnerSearchItem) => {
      setEmail(item.email);
      closeSuggestions();
      inputRef.current?.focus();
    },
    [closeSuggestions]
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setResult(null);
    setConfirmOpen(true);
  };

  const doReset = async () => {
    setError(null);
    setResult(null);
    try {
      const response = await mutation.mutateAsync({ email: email.trim() });
      setResult(response);
      setConfirmOpen(false);
    } catch (err) {
      setError(getErrorMessage(err));
      setConfirmOpen(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
    } catch {
      // ignore
    }
  };

  const showList =
    suggestionsOpen && debouncedQuery.length >= 2 && (suggestions.length > 0 || isFetching || isError);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showList && e.key === "ArrowDown" && email.trim().length >= 2) {
      setSuggestionsOpen(true);
      return;
    }
    if (!showList || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0 && suggestions[activeIndex]) {
      e.preventDefault();
      selectOwner(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      closeSuggestions();
    }
  };

  const inner = (
    <>
      <h2>Reset location manager password</h2>
      <p>
        Send a temporary password to a location manager so they can sign in and update it on first use. Search by
        email or enter the full address.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="settings-info-callout" style={{ marginBottom: "0.15rem" }}>
          <span aria-hidden style={{ fontWeight: 800 }}>
            ⓘ
          </span>
          <span>A temporary password will be generated and should be shared securely.</span>
        </div>

        <div ref={containerRef} style={{ position: "relative" }}>
          <label style={{ display: "grid", gap: "0.35rem" }}>
            <span className="settings-label">Location manager email</span>
            <div className="settings-field-with-suffix">
              <input
                ref={inputRef}
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setSuggestionsOpen(true);
                  setActiveIndex(-1);
                }}
                onFocus={() => setSuggestionsOpen(true)}
                onBlur={() => {
                  window.setTimeout(() => {
                    if (!containerRef.current?.contains(document.activeElement)) {
                      closeSuggestions();
                    }
                  }, 150);
                }}
                onKeyDown={onKeyDown}
                style={inputStyle}
                placeholder="Search or enter email"
                autoComplete="off"
                required
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showList}
                aria-controls={listboxId}
              />
              <span className="settings-field-suffix-icon" aria-hidden>
                🔍
              </span>
            </div>
          </label>

          {showList ? (
            <ul
              id={listboxId}
              role="listbox"
              style={{
                position: "absolute",
                zIndex: 20,
                left: 0,
                right: 0,
                top: "100%",
                margin: "0.25rem 0 0 0",
                padding: 0,
                listStyle: "none",
                maxHeight: "220px",
                overflowY: "auto",
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "0.5rem",
                boxShadow: "0 12px 24px rgba(15, 23, 42, 0.12)"
              }}
            >
              {isFetching ? (
                <li style={{ padding: "0.6rem 0.75rem", color: "#64748b", fontSize: "0.9rem" }}>Searching…</li>
              ) : isError ? (
                <li style={{ padding: "0.6rem 0.75rem", color: "#b91c1c", fontSize: "0.9rem" }}>
                  Search failed. Try again or type the full email.
                </li>
              ) : suggestions.length === 0 ? (
                <li style={{ padding: "0.6rem 0.75rem", color: "#64748b", fontSize: "0.9rem" }}>
                  No matching owners. Enter the full email if it is not listed.
                </li>
              ) : (
                suggestions.map((row, index) => (
                  <li key={row.email} role="option" aria-selected={index === activeIndex}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectOwner(row)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "0.55rem 0.75rem",
                        fontSize: "0.9rem",
                        border: "none",
                        background: index === activeIndex ? "#eff6ff" : "transparent",
                        cursor: "pointer",
                        color: "#0f172a"
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{row.email}</div>
                      <div style={{ color: "#64748b", fontSize: "0.8rem" }}>
                        {row.first_name} {row.last_name}
                      </div>
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>

        {error ? <div style={{ color: "#b91c1c", fontSize: "0.875rem" }}>{error}</div> : null}

        {result ? (
          <div
            style={{
              border: "1px solid #cbd5f5",
              background: "#f8fafc",
              borderRadius: "0.75rem",
              padding: "0.9rem"
            }}
          >
            <div style={{ fontWeight: 650, color: "#0f172a" }}>New temporary password</div>
            <div style={{ marginTop: "0.35rem", color: "#334155" }}>
              Email: <strong>{result.email}</strong>
            </div>
            <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
              <code
                style={{
                  padding: "0.35rem 0.5rem",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.5rem"
                }}
              >
                {result.temp_password}
              </code>
              <button
                type="button"
                onClick={copyPassword}
                className="settings-btn-primary"
                style={{ padding: "0.4rem 0.85rem", fontSize: "0.82rem" }}
              >
                Copy
              </button>
            </div>
            <div style={{ marginTop: "0.6rem", color: "#64748b", fontSize: "0.85rem" }}>
              This password is shown once. Store it securely and share it with the owner via an approved channel.
            </div>
          </div>
        ) : null}

        <div className="settings-form-actions">
          <button type="submit" disabled={!canSubmit} className="settings-btn-primary">
            {mutation.isPending ? "Working…" : "Generate reset"}
          </button>
        </div>
      </form>

      {confirmOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.25rem",
            zIndex: 100
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "520px",
              background: "#fff",
              borderRadius: "0.85rem",
              border: "1px solid #e2e8f0",
              boxShadow: "0 30px 60px rgba(15, 23, 42, 0.18)",
              padding: "1.25rem"
            }}
          >
            <div style={{ fontWeight: 750, fontSize: "1.1rem" }}>Confirm password reset</div>
            <div style={{ marginTop: "0.5rem", color: "#475569" }}>
              Reset password for <strong>{email.trim()}</strong>? This will invalidate the current password and generate a
              new temporary password.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.25rem" }}>
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                style={{
                  padding: "0.55rem 1.1rem",
                  borderRadius: "0.5rem",
                  border: "1px solid #cbd5f5",
                  background: "#fff",
                  cursor: "pointer",
                  fontWeight: 600
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doReset}
                style={{
                  padding: "0.55rem 1.1rem",
                  borderRadius: "0.5rem",
                  border: "none",
                  background: "#dc2626",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 700
                }}
              >
                Yes, reset
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return <div className="settings-form-embedded">{inner}</div>;
  }

  return <div className="settings-form-card">{inner}</div>;
};

export default AdminResetOwnerPasswordForm;
